import { action, flow, makeObservable, observable } from 'mobx';
import { GlobalStore } from '@roc/feature-app-core';
import { downloadDocument } from '@roc/feature-utils';
import { LienFormStore } from './lienFormStore';
import { LienPayoffReviewService } from '../service/lienPayoffReviewService';
import { LoanPropertyOption, PAYOFF_LETTER_LIEN_SOURCE, PropertyLien, PropertyLienPayoff } from '../components/lienPayoffReview/lienPayoffReviewCard';

export class LienPayoffReviewStore {
  globalStore: GlobalStore;
  private lienPayoffReviewService: LienPayoffReviewService;

  loanId: number | null;
  propertyId: number | null;
  records: PropertyLienPayoff[];
  processingStatus: string;
  loanProperties: LoanPropertyOption[];
  lienFormStore: LienFormStore;
  // The payoff letter open in the PDF preview, same shape (and type) as DocumentStore.documentPreview.
  letterPreview: any;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.lienPayoffReviewService = new LienPayoffReviewService();
    this.loanId = null;
    this.propertyId = null;
    this.records = [];
    this.processingStatus = null;
    this.loanProperties = [];
    this.lienFormStore = new LienFormStore(globalStore);
    this.letterPreview = null;

    makeObservable(this, {
      loanId: observable,
      propertyId: observable,
      records: observable,
      processingStatus: observable,
      loanProperties: observable,
      getScreenData: flow,
      uploadLienPayoffFile: flow,
      letterPreview: observable.ref,
      previewLienPayoffFile: flow,
      closeLetterPreview: action,
      deletePayoff: flow,
      saveLien: flow,
      loadForm: action,
      getLienById: action,
    });
  }

  *getScreenData(loanId: number, propertyId?: number) {
    this.loanId = loanId;
    this.propertyId = propertyId ?? null;
    try {
      const response = yield this.lienPayoffReviewService.getScreenData(loanId, propertyId);
      const data = response?.data;
      this.records = data?.liens ?? [];
      this.processingStatus = data?.processingStatus ?? null;
      this.loanProperties = Array.isArray(data?.loanProperties)
        ? data.loanProperties.map(p => ({ propertyId: p.propertyId, fullAddress: p.fullAddress }))
        : [];
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while loading the payoff review data',
      });
    }
  }

  getLienById(lienId: number): PropertyLien | undefined {
    return this.records.find(r => r.lien.propertyLienId === lienId)?.lien;
  }

  // Populates the Add/Edit lien form (field set mirrors Title Commitment's own, since both
  // operate on the same property_liens table) from an existing lien, or resets it for a
  // brand-new one.
  loadForm(lienId: number | null | undefined) {
    this.lienFormStore.reset();
    const lien = lienId != null ? this.getLienById(lienId) : undefined;
    this.lienFormStore.loadForm({
      originalAmount: lien?.originalAmount,
      madeTo: lien?.madeTo,
      // null, not undefined: the date picker shows today's date for undefined, but nothing is saved.
      datedDate: lien?.datedDate ?? null,
      recordedDate: lien?.recordedDate ?? null,
      madeBy: lien?.madeBy,
      lienType: lien?.lienType,
    });
  }

  *saveLien(editingLienId?: number | null) {
    try {
      this.lienFormStore.runFormValidation();
      if (!this.lienFormStore.form.meta.isValid) {
        throw new Error();
      }
    } catch (e) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please fix the validation errors',
      });
      throw e;
    }

    try {
      const { originalAmount, madeTo, datedDate, recordedDate, madeBy, lienType } =
        this.lienFormStore.getFormValues();
      const existingLien = editingLienId != null ? this.getLienById(editingLienId) : undefined;
      // A payoff belongs to the Payoff task's property (an edit keeps the lien's own property).
      const resolvedPropertyId =
        existingLien?.propertyId ??
        this.propertyId ??
        (this.loanProperties.length === 1 ? this.loanProperties[0].propertyId : null);

      // Only the amount is required; the other blanks (lien type included) go as null.
      const lien: Partial<PropertyLien> = {
        propertyLienId: editingLienId ?? undefined,
        originalAmount: originalAmount === '' ? null : originalAmount,
        madeTo: madeTo || null,
        datedDate: datedDate || null,
        recordedDate: recordedDate || null,
        madeBy: madeBy || null,
        lienType: lienType || null,
        loanId: this.loanId,
        propertyId: resolvedPropertyId ?? undefined,
        sourceType: PAYOFF_LETTER_LIEN_SOURCE,
      };
      yield this.lienPayoffReviewService.saveLien(lien);

      if (this.loanId) {
        yield this.getScreenData(this.loanId, this.propertyId ?? undefined);
      }
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while saving the lien',
      });
      throw new Error();
    }
  }

  *uploadLienPayoffFile(file: File, propertyLienId: number, lienPayoffRecordId?: number) {
    try {
      yield this.lienPayoffReviewService.uploadLienPayoffFile(file, propertyLienId, lienPayoffRecordId);
      if (this.loanId) {
        yield this.getScreenData(this.loanId, this.propertyId ?? undefined);
      }
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while uploading the payoff letter',
      });
      throw new Error();
    }
  }

  // Opens the letter in the same PDF preview as the trailing docs / Title Commitment documents;
  // other file types (e.g. Word letters) can't be previewed, so they're downloaded instead.
  *previewLienPayoffFile(lienPayoffRecordId: number, originalFileName?: string) {
    try {
      const response = yield this.lienPayoffReviewService.downloadLienPayoffFile(lienPayoffRecordId);
      const fileName = originalFileName || 'payoff_letter.pdf';
      if (!fileName.toLowerCase().endsWith('.pdf')) {
        downloadDocument(response?.data, { ...response?.headers }, 'download', fileName);
        return;
      }
      this.letterPreview = {
        title: fileName,
        data: new File([response?.data], fileName, { type: 'application/pdf' }),
        headers: { ...response?.headers, 'content-type': 'application/pdf' },
      };
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while opening the payoff letter',
      });
    }
  }

  closeLetterPreview() {
    this.letterPreview = null;
  }

  // Deletes the whole payoff: the lien and its uploaded letters.
  *deletePayoff(propertyLienId: number) {
    try {
      yield this.lienPayoffReviewService.deletePayoff(propertyLienId);
      if (this.loanId) {
        yield this.getScreenData(this.loanId, this.propertyId ?? undefined);
      }
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while deleting the payoff',
      });
      throw new Error();
    }
  }
}
